/**
 * Canonical box-shadows for the Apollo design system.
 *
 * Figma's shadow frame (node `23656:13013`), read variable by variable on 2026-09-21:
 * `MUI/shadowXsmall`, `MUI/shadowSmall`, `MUI/shadowMedium`, `MUI/shadowLarge` for depth,
 * `MUI/focusRing/*` for the rings.
 * The depth steps are named on the same `xs … lg` scale as `borderRadius` and the theme's `TYPE_SCALE`,
 * so one vocabulary covers every scale in the system.
 *
 * Figma lists the tighter layer of a two-layer shadow first and CSS here lists the wider one first.
 * The composite is identical — worth knowing before comparing the two side by side.
 *
 * **Depth and focus are not one scale.** The rings carry no offset or blur, and only the depth steps
 * reach MUI's `elevation` ladder (`theme/shadows.ts`). The two rings here are the ones whose colour has
 * no palette slot; a ring that follows a slot — Figma's third, `MUI/focusRing/outline`, on
 * `primary/main` — is built at the call site by `focusRing` so it cannot drift from the palette.
 */

import { colors } from "./colors";

export const shadows = {
  /** `MUI/shadowXsmall`. A resting button — rung 1 of `theme/shadows.ts` — and the control bar. */
  xs: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  /**
   * `MUI/shadowSmall`. A focused button, and the floor under MUI's own shallow elevations (rungs 2-8
   * of `theme/shadows.ts`).
   */
  sm: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
  /**
   * `MUI/shadowMedium`. Dialogs, cards and popovers.
   *
   * Cards used to carry a `raised` step of their own — the same first layer over a softer second
   * (`0.06`, spread `-1`). Figma's frame ships four depths and none of them is it, so the two merged.
   */
  md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
  /**
   * `MUI/shadowLarge`. Floating panels: menus and dropdowns.
   *
   * Deliberately absent from the `elevation` ladder. Its rung would be 8 — which `Popover` defaults to
   * but a pressed `Button` also lands on — so every floating surface names this token itself.
   */
  lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",

  /**
   * `MUI/focusRing/Focussed`. The key keeps the `input` prefix that Figma's variable name has since
   * dropped — the legacy Tailwind `shadow-input` utility is still on a dozen unmigrated fields.
   */
  input: `0px 0px 0px 2px ${colors.aqua[100]}`,
  /** `MUI/focusRing/Error`. */
  "input-invalid": `0px 0px 0px 2px ${colors.critical[100]}`,
};
